import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { mediaUrl } from '../Prompts/promptsV2Utils';

interface UploadSupportingDocumentProps {
  documentFiles: { [key: number]: File };
  setDocumentFiles: Dispatch<SetStateAction<{ [key: number]: File }>>;
  openPromptIndex: number | null;
  variant?: string;
  link?: string;
  showLink?: boolean;
}

const UploadSupportingDocument: React.FC<UploadSupportingDocumentProps> = ({
  documentFiles,
  setDocumentFiles,
  openPromptIndex,
  variant,
  link,
  showLink,
}) => {
  const documentInputRef = useRef<HTMLInputElement | null>(null);

  const triggerDocumentUpload = () => {
    documentInputRef.current?.click();
  };

  const handleDocumentUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : null;

    if (file && openPromptIndex !== null) {
      setDocumentFiles((prevFiles) => ({
        ...prevFiles,
        [openPromptIndex]: file,
      }));
      toast.success(`${file.name} uploaded successfully!`);
    }

    event.target.value = '';
  };

  const handleDeleteDocument = (index: number) => {
    setDocumentFiles((prevFiles) => {
      const updatedFiles = { ...prevFiles };
      //  const fileName = updatedFiles[index]?.name;
      delete updatedFiles[index];
      return updatedFiles;
    });
    toast.success('Document deleted successfully!');
  };

  const uploadedFile = openPromptIndex !== null ? documentFiles[openPromptIndex] : null;

  const remoteDocumentUrl = useMemo(() => (link ? mediaUrl(link) : ''), [link]);

  const localPreviewUrl = useMemo(() => {
    if (!uploadedFile) return '';
    return URL.createObjectURL(uploadedFile);
  }, [uploadedFile]);

  useEffect(() => {
    return () => {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
    };
  }, [localPreviewUrl]);

  const viewDocumentUrl = localPreviewUrl || remoteDocumentUrl;

  const handleViewDocument = () => {
    if (!viewDocumentUrl) {
      toast.info('No document available yet. Choose a file to upload first.');
      return;
    }
    window.open(viewDocumentUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <input
        type="file"
        title="Upload Supporting Document"
        ref={documentInputRef}
        className="support-doc-hidden-input"
        onChange={handleDocumentUpload}
        accept=".pdf,.doc,.docx,.txt"
      />

      {(variant === 'modal' || variant === 'buttons') && (
        <div className="support-doc-uploader">
          <div className="support-doc-card">
            <div>
              <div className="support-doc-title">Upload Supporting Document</div>
              <div className="support-doc-caption">PDF, DOC, DOCX, or TXT files are supported.</div>
            </div>
            <Button variant="success" onClick={triggerDocumentUpload}>
              {uploadedFile ? 'Replace file' : 'Choose file'}
            </Button>
          </div>

          {(showLink || uploadedFile) && (
            <button
              type="button"
              className="support-doc-link border-0 bg-transparent p-0"
              onClick={handleViewDocument}
              disabled={!viewDocumentUrl}
            >
              View document
            </button>
          )}

          {uploadedFile && (
            <div className="support-doc-file-row">
              <span className="support-doc-file-name">{uploadedFile.name}</span>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleDeleteDocument(openPromptIndex!)}
              >
                Remove
              </Button>
            </div>
          )}
        </div>
      )}

      {variant !== 'modal' && variant !== 'buttons' && (
        <div className="d-flex flex-column align-items-center gap-3 mt-3 w-100">
          {(showLink || uploadedFile) && (
            <button
              type="button"
              className="w-100 prompt-collapse-btn"
              onClick={handleViewDocument}
              disabled={!viewDocumentUrl}
            >
              View Document
            </button>
          )}

          <div className="d-flex gap-3">
            <Button variant="success" onClick={triggerDocumentUpload} disabled={!!uploadedFile}>
              Upload Supporting Document
            </Button>

            {uploadedFile && (
              <Button variant="danger" onClick={() => handleDeleteDocument(openPromptIndex!)}>
                Delete
              </Button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default UploadSupportingDocument;
